import PropTypes from "prop-types";
import ReactApexChart from "react-apexcharts";
// @mui
import { useTheme, styled } from "@mui/material/styles";
import { Card, CardHeader } from "@mui/material";
// utils
import { fNumber } from "../../../utils/formatNumber"; // Assuming this formats numbers nicely
// components
import { useChart } from "../../../components/chart"; // Your custom chart hook

// ----------------------------------------------------------------------

const CHART_HEIGHT = 372; // Keep this height consistent for the wrapper

const StyledChartWrapper = styled("div")(({ theme }) => ({
	height: CHART_HEIGHT,
	marginTop: theme.spacing(5),
	"& .apexcharts-canvas svg": { height: CHART_HEIGHT },
	"& .apexcharts-canvas svg,.apexcharts-canvas foreignObject": {
		overflow: "visible", // Ensure content is not clipped
	},
	// Add some basic styling for the chart container for better visual appeal
	padding: theme.spacing(2),
	borderRadius: theme.shape.borderRadius,
	backgroundColor: theme.palette.background.paper,
}));

// ----------------------------------------------------------------------

AppCurrentVisits.propTypes = {
	title: PropTypes.string,
	subheader: PropTypes.string,
	chartColors: PropTypes.arrayOf(PropTypes.string),
	chartData: PropTypes.array, // Expecting [{ label: 'Category', value: 10 }]
};

export default function AppCurrentVisits({ title, subheader, chartColors, chartData, ...other }) {
	const theme = useTheme();

	// Prepare data for the treemap series
	// ApexCharts treemap expects data in the format: [{ data: [{ x: 'label', y: value }] }]
	const chartSeries = [
		{
			data: chartData.map((item) => ({
				x: item.label, // Category name
				y: item.value, // Value
			})),
		},
	];

	const chartOptions = useChart({
		colors: chartColors, // Use the provided chartColors
		legend: { show: false }, // Treemaps usually don't need a legend as labels are on slabs

		// --- IMPROVED TOOLTIP FOR CLARITY ---
		tooltip: {
			enabled: true, // Ensure tooltips are enabled
			fillSeriesColor: false, // Don't fill tooltip background with series color
			y: {
				formatter: (value, { series, seriesIndex, dataPointIndex, w }) => {
					// Get the label for the current data point
					const label = w.config.series[seriesIndex].data[dataPointIndex].x;
					// Calculate total for percentage if needed (optional)
					const total = chartData.reduce((sum, item) => sum + item.value, 0);
					const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;

					return `${label}: ${fNumber(value)} (${percentage}%)`;
				},
				title: {
					formatter: (seriesName) => "", // Hide default series title, we include it in y formatter
				},
			},
			style: {
				fontSize: "12px",
				fontFamily: "Inter, sans-serif", // Use Inter font
			},
		},

		// --- ENHANCED DATA LABELS FOR VISIBILITY ---
		dataLabels: {
			enabled: true,
			// Style for the data labels within the treemap slabs
			style: {
				fontSize: "13px", // Slightly smaller font for better fit
				fontWeight: "bold",
				colors: [theme.palette.text.primary], // Use theme's primary text color for better contrast
			},
			// Drop shadow for better readability against varied background colors
			dropShadow: {
				enabled: false, // Changed to false to remove the shadow
				top: 1,
				left: 1,
				blur: 1,
				color: "#000",
				opacity: 0.45, // Slightly more opaque shadow
			},
			// Formatter to display both label and value/percentage
			formatter: function (text, { series, seriesIndex, dataPointIndex, w }) {
				const value = w.config.series[seriesIndex].data[dataPointIndex].y;
				const total = chartData.reduce((sum, item) => sum + item.value, 0);
				const percentage = total > 0 ? ((value / total) * 100).toFixed(0) : 0; // Rounded percentage

				// Display label and percentage/value, adjust as needed for small spaces
				if (percentage < 5 && value > 0) { // For very small segments, just show value or a short label
					return [text, `${fNumber(value)}`];
				}
				return [text, `${fNumber(value)} (${percentage}%)`];
			},
		},

		plotOptions: {
			treemap: {
				distributed: true, // Ensures each top-level segment gets a distinct color from the 'colors' array
				enableShades: true, // Adds a subtle gradient for visual depth
				// --- CRUCIAL FOR SMALL LABELS ---
				dataLabels: {
					format: "scale", // Formats the data labels based on the scale of the treemap
					// minHeightForSlab: This is the most important property for small labels.
					// It defines the minimum height (in pixels) a slab must have for its data label to be displayed.
					// Set it to a very small number or 0 to show labels on tiny segments.
					minHeightForSlab: 0, // Show labels even for very small slabs
				},
			},
		},
		// Add responsive options to adjust font sizes on smaller screens
		responsive: [
			{
				breakpoint: 600, // For screens smaller than 600px
				options: {
					dataLabels: {
						style: {
							fontSize: "10px", // Smaller font for labels on mobile
						},
						formatter: function (text, { series, seriesIndex, dataPointIndex, w }) {
							const value = w.config.series[seriesIndex].data[dataPointIndex].y;
							// On small screens, for very small segments, just show the label or hide if too cramped
							if (value < 10) { // Adjust this threshold as needed
								return text; // Just the label
							}
							return [text, `${fNumber(value)}`]; // Label and value
						},
					},
					tooltip: {
						style: {
							fontSize: "10px",
						},
					},
				},
			},
		],
	});

	return (
		<Card {...other}>
			<CardHeader title={title} subheader={subheader} />

			<StyledChartWrapper dir="ltr">
				<ReactApexChart
					type="treemap"
					series={chartSeries}
					options={chartOptions}
					height={320} // Adjusted height to give more room for the chart itself
				/>
			</StyledChartWrapper>
		</Card>
	);
}